<template>
  <div
    :class="cardClasses"
    v-bind="$attrs"
    @click="handleClick"
    @keydown="handleKeydown"
  >
    <!-- Header section -->
    <div
      v-if="$slots.header || title || $slots.title"
      class="card-header"
      :class="headerClasses"
    >
      <div v-if="title || $slots.title" class="flex items-center justify-between">
        <!-- Title content -->
        <div class="flex items-center min-w-0 flex-1">
          <component
            v-if="headerIcon"
            :is="headerIcon"
            class="h-5 w-5 text-legal-500 mr-2 flex-shrink-0"
            aria-hidden="true"
          />
          <h3
            v-if="title"
            class="text-lg font-semibold text-legal-900 truncate"
            :class="titleClasses"
          >
            {{ title }}
          </h3>
          <slot v-else name="title" />
        </div>
        
        <!-- Header actions -->
        <div v-if="$slots.actions" class="flex items-center space-x-2 ml-4">
          <slot name="actions" />
        </div>
      </div>
      
      <!-- Custom header content -->
      <slot v-if="$slots.header" name="header" />
      
      <!-- Subtitle -->
      <p
        v-if="subtitle"
        class="mt-1 text-sm text-legal-600"
        :class="subtitleClasses"
      >
        {{ subtitle }}
      </p>
    </div>

    <!-- Body/Content section -->
    <div
      v-if="$slots.default"
      class="card-body"
      :class="bodyClasses"
    >
      <slot />
    </div>

    <!-- Footer section -->
    <div
      v-if="$slots.footer"
      class="card-footer"
      :class="footerClasses"
    >
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, useSlots } from 'vue'

// Props definition
const props = defineProps({
  // Card variants and styling
  variant: {
    type: String,
    default: 'default',
    validator: (value) => [
      'default', 'elevated', 'outlined', 'ghost', 
      'success', 'warning', 'error', 'info',
      'glass-light', 'professional'
    ].includes(value)
  },
  
  // Card layout and spacing  
  padding: {
    type: [Boolean, String],
    default: true,
    validator: (value) => 
      typeof value === 'boolean' || 
      ['none', 'small', 'medium', 'large'].includes(value)
  },
  noPadding: {
    type: Boolean,
    default: false
  },
  
  // Card appearance
  shadow: {
    type: [Boolean, String],
    default: true,
    validator: (value) => 
      typeof value === 'boolean' || 
      ['none', 'small', 'medium', 'large'].includes(value)
  },
  rounded: {
    type: [Boolean, String],
    default: true,
    validator: (value) => 
      typeof value === 'boolean' || 
      ['none', 'small', 'medium', 'large', 'full'].includes(value)
  },
  
  // Header content
  title: {
    type: String,
    default: null
  },
  subtitle: {
    type: String,
    default: null
  },
  headerIcon: {
    type: [String, Object],
    default: null
  },
  
  // Interactive behavior
  clickable: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  
  // Custom classes
  headerClass: {
    type: [String, Array, Object],
    default: null
  },
  bodyClass: {
    type: [String, Array, Object],
    default: null
  },
  footerClass: {
    type: [String, Array, Object],
    default: null
  }
})

// Events
const emit = defineEmits(['click', 'keydown'])

// Access slots
const slots = useSlots()

// Computed classes following Basket Base patterns
const cardClasses = computed(() => {
  const classes = [
    // Base styles
    'bg-white overflow-hidden transition-all duration-200',
    
    // Interactive states
    {
      'cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2': props.clickable && !props.disabled,
      'hover:shadow-lg': props.clickable && !props.disabled && !['glass-light', 'professional'].includes(props.variant),
      'opacity-60 cursor-not-allowed': props.disabled,
      'animate-pulse': props.loading
    }
  ]

  // Shadow variants
  if (props.shadow !== false && props.shadow !== 'none') {
    if (props.shadow === 'small' || props.shadow === true) {
      classes.push('shadow-sm')
    } else if (props.shadow === 'medium') {
      classes.push('shadow-md')
    } else if (props.shadow === 'large') {
      classes.push('shadow-lg')
    }
  }

  // Rounded corners
  if (props.rounded !== false && props.rounded !== 'none') {
    if (props.rounded === 'small') {
      classes.push('rounded-sm')
    } else if (props.rounded === 'medium' || props.rounded === true) {
      classes.push('rounded-lg')
    } else if (props.rounded === 'large') {
      classes.push('rounded-xl')
    } else if (props.rounded === 'full') {
      classes.push('rounded-full')
    }
  }

  // Variant-specific styles
  switch (props.variant) {
    case 'elevated':
      classes.push('shadow-lg border-0')
      break
    case 'outlined':
      classes.push('border border-legal-200 shadow-none')
      break
    case 'ghost':
      classes.push('bg-transparent shadow-none border-0')
      break
    case 'success':
      classes.push('border-l-4 border-l-green-500 bg-green-50')
      break
    case 'warning':
      classes.push('border-l-4 border-l-yellow-500 bg-yellow-50')
      break
    case 'error':
      classes.push('border-l-4 border-l-red-500 bg-red-50')
      break
    case 'info':
      classes.push('border-l-4 border-l-primary-500 bg-primary-50')
      break
    case 'glass-light':
      classes.push(
        'glass-card-light',
        'hover:shadow-xl',
        'ring-1 ring-white/20',
        'card-hover-lift'
      )
      break
    case 'professional':
      classes.push(
        'card-base',
        'card-hover',
        'ring-1 ring-legal-200/50',
        'hover:border-accent-200'
      )
      break
    default:
      classes.push('border border-legal-100')
  }

  return classes
})

const headerClasses = computed(() => {
  const classes = []
  
  // Padding
  if (!props.noPadding) {
    if (props.padding === 'small') {
      classes.push('px-4 py-3')
    } else if (props.padding === 'large') {
      classes.push('px-8 py-6')
    } else {
      classes.push('px-6 py-4') // medium/default
    }
  }
  
  // Border
  if (slots.default || slots.footer) {
    classes.push('border-b border-legal-100')
  }
  
  // Custom classes
  if (props.headerClass) {
    classes.push(props.headerClass)
  }
  
  return classes
})

const bodyClasses = computed(() => {
  const classes = []
  
  // Custom classes
  if (props.bodyClass) {
    classes.push(props.bodyClass)
  }
  
  return classes
})

const footerClasses = computed(() => {
  const classes = ['border-t border-legal-100 bg-legal-50']
  
  // Padding
  if (!props.noPadding) {
    if (props.padding === 'small') {
      classes.push('px-4 py-3')
    } else if (props.padding === 'large') {
      classes.push('px-8 py-6')
    } else {
      classes.push('px-6 py-4') // medium/default
    }
  }
  
  // Custom classes
  if (props.footerClass) {
    classes.push(props.footerClass)
  }
  
  return classes
})

const titleClasses = computed(() => {
  const classes = []
  
  // Variant-specific title colors
  switch (props.variant) {
    case 'success':
      classes.push('text-green-800')
      break
    case 'warning':
      classes.push('text-yellow-800')
      break
    case 'error':
      classes.push('text-red-800')
      break
    case 'info':
      classes.push('text-primary-800')
      break
    default:
      classes.push('text-legal-900')
  }
  
  return classes
})

const subtitleClasses = computed(() => {
  const classes = []
  
  // Variant-specific subtitle colors
  switch (props.variant) {
    case 'success':
      classes.push('text-green-600')
      break
    case 'warning':
      classes.push('text-yellow-600')
      break
    case 'error':
      classes.push('text-red-600')
      break
    case 'info':
      classes.push('text-primary-600')
      break
    default:
      classes.push('text-legal-600')
  }
  
  return classes
})

// Event handlers
const handleClick = (event) => {
  if (props.disabled || props.loading) {
    event.preventDefault()
    return
  }
  
  if (props.clickable) {
    emit('click', event)
  }
}

const handleKeydown = (event) => {
  if (props.disabled || props.loading) {
    return
  }
  
  if (props.clickable && (event.key === 'Enter' || event.key === ' ')) {
    event.preventDefault()
    emit('click', event)
  }
  
  emit('keydown', event)
}
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Loading animation */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Smooth transitions using design system */
.transition-all {
  transition: all var(--transition-base);
}

/* Focus ring styling */
.focus\:ring-2:focus {
  --tw-ring-width: 2px;
}

.focus\:ring-offset-2:focus {
  --tw-ring-offset-width: 2px;
}

/* Hover transform effect using design system */
.hover\:-translate-y-0\.5:hover {
  transform: translate3d(0, -0.125rem, 0);
}
</style>
